import * as React from 'react'
import ReactSelect, { SelectInstance, Props as SelectProps } from 'react-select'
import { Colors, FontSizePx } from '@kvika/audur-theme'
import { useRef } from 'react'

import { useOutsideAlerter } from '../../utils/Utils'

import { DropdownIndicator, OptionComponent, SelectContainer } from './Components'
import { Option, SelectWidth } from './Constants'
import { getCustomStyles, SelectWrapper } from './styles/SelectStyles'

type LabelProps = { label?: undefined; inputId?: string } | { label: React.ReactNode; inputId: string }
interface Props extends Omit<
	SelectProps<Option | Option[] | null>,
	'getOptionValue' | 'getOptionLabel' | 'styles' | 'components' | 'onChange'
> {
	width?: SelectWidth | string
	value?: Option | Option[] | null
	options?: Array<Option>
	valueKey?: string
	labelKey?: string
	onChange?: (option: Option | Option[] | null) => void
	label?: React.ReactNode
	clearText?: React.ReactNode
	backgroundColor?: Colors | string
	color?: Colors | string
	fontSize?: FontSizePx
	textColor?: Colors | string
	hidePadding?: boolean
	labelOnly?: boolean
	fullSize?: boolean
	onLabelMenuOpen?: (newState: boolean) => void
}

const Select = ({
	width = SelectWidth.MEDIUM,
	value = null,
	options = [],
	valueKey = 'value',
	labelKey = 'label',
	label,
	clearText = 'Hreinsa',
	onChange,
	backgroundColor,
	color,
	fontSize,
	textColor,
	hidePadding = false,
	labelOnly = false,
	fullSize = false,
	onLabelMenuOpen,
	...rest
}: Props & LabelProps): JSX.Element => {
	const [isKeyboardFocused, setIsKeyboardFocused] = React.useState<boolean>(false)
	const [isUsingKeyboard, setIsUsingKeyboard] = React.useState<boolean>(false)
	const [manuallyOpen, setManuallyOpen] = React.useState<boolean>(false)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const selectRef = useRef<SelectInstance<Option, boolean>>(null)

	React.useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				setIsUsingKeyboard(true)
			}
		}

		const handleMouseDown = () => {
			setIsUsingKeyboard(false)
		}

		window.addEventListener('keydown', handleKeyDown)
		window.addEventListener('mousedown', handleMouseDown)

		return () => {
			window.removeEventListener('keydown', handleKeyDown)
			window.removeEventListener('mousedown', handleMouseDown)
		}
	}, [])

	const handleFocus = () => {
		if (isUsingKeyboard) {
			setIsKeyboardFocused(true)
		}
	}

	const handleBlur = () => {
		setIsKeyboardFocused(false)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		// When menu is open, use tab to navigate options
		if (e.key === 'Tab' && manuallyOpen) {
			e.preventDefault()
			if (selectRef.current) {
				selectRef.current.focusOption('down')
			}
			return
		}

		// When menu is open, prevent Enter from toggling the button
		if (e.key === 'Enter' && manuallyOpen && selectRef.current) {
			const focusedOption = selectRef.current.state?.focusedOption
			if (focusedOption) {
				e.preventDefault()
				onSelectChange(focusedOption)
			}
		}
	}

	useOutsideAlerter(
		wrapperRef,
		() => {
			setManuallyOpen(false)
			onLabelMenuOpen?.(false)
		},
		wrapperRef
	)

	const onSelectChange = (newValue: Option | unknown | null) => {
		if (labelOnly && manuallyOpen) {
			setManuallyOpen(false)
		}

		if (onChange) {
			onChange(newValue as Option | Option[] | null)
		}
	}

	return (
		<SelectWrapper isKeyboardFocused={isKeyboardFocused} hidePadding={hidePadding} ref={wrapperRef}>
			<ReactSelect<Option, boolean>
				{...rest}
				ref={selectRef}
				styles={getCustomStyles({ width, backgroundColor, color, fontSize, textColor, labelOnly, fullSize })}
				onChange={onSelectChange}
				tabSelectsValue={false}
				value={value}
				options={options}
				onKeyDown={handleKeyDown}
				closeMenuOnSelect={rest.closeMenuOnSelect || labelOnly ? true : !rest.isMulti}
				closeMenuOnScroll={
					rest.closeMenuOnScroll || labelOnly
						? () => {
								setManuallyOpen(false)
								onLabelMenuOpen?.(false)
								return true
							}
						: false
				}
				getOptionValue={(option: Option) => option[valueKey] as string}
				getOptionLabel={(option: Option) => option[labelKey] as string}
				openMenuOnClick
				hideSelectedOptions={false}
				isOptionSelected={(option: Option) => {
					return option.isSelected ?? false
				}}
				menuPosition='fixed'
				placeholder='Veldu'
				noOptionsMessage={() => 'Engir valkostir tiltækir'}

				// @ts-ignore
				label={label}
				clearText={clearText}
				chevronColor={textColor}
				onMenuOpen={() => {
					if (labelOnly) {
						setManuallyOpen(!manuallyOpen)
						onLabelMenuOpen?.(!manuallyOpen)
					}
				}}
				onMenuClose={() => {
					if (labelOnly) {
						setManuallyOpen(false)
						onLabelMenuOpen?.(false)
					}
				}}
				menuIsOpen={labelOnly ? manuallyOpen : undefined}
				components={{
					DropdownIndicator,
					SelectContainer,
					...(rest.isMulti && { Option: OptionComponent }),
				}}
				labelOnly={labelOnly}
				onFocus={handleFocus}
				onBlur={handleBlur}
			/>
		</SelectWrapper>
	)
}

export default Select
